import { routineLogSummary } from './routineLogSummary';
import { APP_CONSTANTS } from '@/config/constants';
import { SupportDiagnosticBuffer, sanitizeSupportDiagnosticString, sanitizeSupportDiagnosticValue } from '@/services/diagnostics/SupportDiagnosticBuffer';
import { MMKV } from 'react-native-mmkv';

type ConsoleMethod = 'log' | 'info' | 'warn' | 'error' | 'debug';
export type CircularLogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug' | 'custom';

export interface CircularLogEvent {
  id: number;
  timestamp: number;
  level: CircularLogLevel;
  tag: string;
  message: string;
  data?: Record<string, any>;
}

const LOG_BUFFER_CONFIG = APP_CONSTANTS.DEBUG.LOG_BUFFER;
const DEFAULT_CAPACITY = 1000;
const PERSISTENCE_STORAGE_ID = 'app-guincho-circular-logs';
const PERSISTENCE_KEY = 'circular_log_snapshot_v1';
const PERSISTENCE_SCHEMA = 'app-guincho-circular-log-buffer/v1' as const;
const DEFAULT_AUTO_FLUSH_MS = 5_000;
const DEFAULT_URGENT_FLUSH_MIN_INTERVAL_MS = 1_000;
const DEFAULT_RETRY_DELAY_MS = 60_000;
const MAX_RETRY_DELAY_MS = 5 * 60_000;
const DEFAULT_MAX_PERSISTED_BYTES = LOG_BUFFER_CONFIG.MAX_PERSISTED_BYTES ?? 2 * 1024 * 1024;
const DEFAULT_MAX_PERSISTED_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface CircularLogStorage {
  set(key: string, value: string): void;
  getString(key: string): string | undefined;
  delete(key: string): void;
}

interface PersistedCircularLogSnapshot {
  schema: typeof PERSISTENCE_SCHEMA;
  identityKey: string;
  persistedAt: number;
  sessionId: string;
  events: CircularLogEvent[];
}

interface CircularLogBufferOptions {
  storage?: CircularLogStorage | null;
  sessionId?: string;
  now?: () => number;
  autoFlushMs?: number;
  urgentFlushMinIntervalMs?: number;
  retryDelayMs?: number;
  maxPersistedBytes?: number;
  maxPersistedAgeMs?: number;
  schedule?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  cancel?: (timer: ReturnType<typeof setTimeout>) => void;
}

export interface CircularLogPersistenceStatus {
  identityBound: boolean;
  dirty: boolean;
  lastFlushAt: number | null;
  persistedEventCount: number;
  droppedByPersistedByteLimit: number;
  consecutiveFailures: number;
}

const createSessionId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// O JSON já escapa surrogates isolados; a conversão nativa evita percorrer cada byte no Hermes.
const utf8ByteLength = (value: string): number => unescape(encodeURIComponent(value)).length;

const isPersistedEvent = (value: unknown): value is CircularLogEvent => {
  if (!value || typeof value !== 'object') return false;
  const event = value as Partial<CircularLogEvent>;
  return Number.isFinite(event.timestamp) &&
    typeof event.level === 'string' &&
    ['log', 'info', 'warn', 'error', 'debug', 'custom'].includes(event.level) &&
    typeof event.tag === 'string' &&
    typeof event.message === 'string';
};

export class CircularLogBuffer {
  private capacity: number;
  private storage: Array<CircularLogEvent | null>;
  private pointer: number = 0;
  private size: number = 0;
  private nextId: number = 1;
  private readonly persistentStorage: CircularLogStorage | null;
  private readonly sessionId: string;
  private readonly now: () => number;
  private readonly autoFlushMs: number;
  private readonly urgentFlushMinIntervalMs: number;
  private readonly retryDelayMs: number;
  private readonly maxPersistedBytes: number;
  private readonly maxPersistedAgeMs: number;
  private readonly schedule: CircularLogBufferOptions['schedule'];
  private readonly cancel: CircularLogBufferOptions['cancel'];
  private identityKey: string | null | undefined;
  private dirty = false;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFlushAt = 0;
  private retryNotBefore = 0;
  private persistedEventCount = 0;
  private droppedByPersistedByteLimit = 0;
  private consecutiveFailures = 0;
  private readonly chunkByteCache = new Map<number, { raw: string; bytes: number }>();
  private cachedChunkBytes = 0;

  constructor(capacity: number, options: CircularLogBufferOptions = {}) {
    this.capacity = Math.max(capacity, DEFAULT_CAPACITY);
    this.storage = new Array(this.capacity).fill(null);
    this.persistentStorage = options.storage ?? null;
    this.sessionId = options.sessionId ?? createSessionId();
    this.now = options.now ?? Date.now;
    this.autoFlushMs = Math.max(100, options.autoFlushMs ?? DEFAULT_AUTO_FLUSH_MS);
    this.urgentFlushMinIntervalMs = Math.max(
      0,
      options.urgentFlushMinIntervalMs ?? DEFAULT_URGENT_FLUSH_MIN_INTERVAL_MS
    );
    this.retryDelayMs = Math.max(1_000, options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS);
    this.maxPersistedBytes = Math.max(256, options.maxPersistedBytes ?? DEFAULT_MAX_PERSISTED_BYTES);
    this.maxPersistedAgeMs = Math.max(1_000, options.maxPersistedAgeMs ?? DEFAULT_MAX_PERSISTED_AGE_MS);
    this.schedule = options.schedule ?? ((callback, delayMs) => setTimeout(callback, delayMs));
    this.cancel = options.cancel ?? (timer => clearTimeout(timer));
  }

  push(event: Omit<CircularLogEvent, 'id'>): void {
    if (this.capacity <= 0) {
      return;
    }
    this.append({
      ...event,
      message: event.message.slice(0, LOG_BUFFER_CONFIG.MAX_MESSAGE_LENGTH),
      id: this.nextId++,
    });
    this.dirty = true;

    const urgent = event.level === 'error';
    if (
      urgent &&
      this.identityKey &&
      (this.lastFlushAt === 0 || this.now() - this.lastFlushAt >= this.urgentFlushMinIntervalMs)
    ) {
      this.flush();
      return;
    }
    this.scheduleFlush();
  }

  private append(event: CircularLogEvent): void {
    this.storage[this.pointer] = event;
    this.pointer = (this.pointer + 1) % this.capacity;
    this.size = Math.min(this.size + 1, this.capacity);
  }

  snapshot(): CircularLogEvent[] {
    if (this.size === 0) {
      return [];
    }
    const result: CircularLogEvent[] = [];
    const start = this.size === this.capacity ? this.pointer : 0;
    for (let i = 0; i < this.size; i += 1) {
      const index = (start + i) % this.capacity;
      const item = this.storage[index];
      if (item) {
        result.push(item);
      }
    }
    return result;
  }

  clear(): void {
    this.resetMemory();
    this.dirty = false;
    this.persistedEventCount = 0;
    this.droppedByPersistedByteLimit = 0;
    this.lastFlushAt = 0;
    this.retryNotBefore = 0;
    this.consecutiveFailures = 0;
    this.cancelScheduledFlush();
    this.deletePersistedSnapshot();
  }

  bindIdentity(identityKey: string | null): number {
    if (identityKey === this.identityKey) {
      return 0;
    }

    if (!identityKey) {
      this.clear();
      this.identityKey = null;
      return 0;
    }

    const bootstrapEvents = !this.identityKey ? this.snapshot() : [];
    if (this.identityKey && this.identityKey !== identityKey) {
      this.clear();
    }

    this.identityKey = identityKey;
    const persisted = this.loadPersistedSnapshot();
    if (!persisted || persisted.identityKey !== identityKey) {
      if (persisted?.identityKey !== identityKey) {
        this.deletePersistedSnapshot();
      }
      this.dirty = bootstrapEvents.length > 0;
      this.scheduleFlush();
      return 0;
    }

    this.resetMemory();
    persisted.events.forEach(event => this.appendRestored(event));
    bootstrapEvents.forEach(event => this.appendRestored(event));
    const restoredCount = Math.min(persisted.events.length, this.capacity);
    this.appendRestored({
      id: 0,
      timestamp: this.now(),
      level: 'custom',
      tag: 'log_buffer.persistence',
      message: 'Buffer circular restaurado do armazenamento local.',
      data: {
        previousSessionId: persisted.sessionId,
        currentSessionId: this.sessionId,
        restoredEvents: restoredCount,
      },
    });
    this.dirty = true;
    this.persistedEventCount = restoredCount;
    this.scheduleFlush();
    return restoredCount;
  }

  flush(): boolean {
    this.cancelScheduledFlush();
    if (!this.persistentStorage || !this.identityKey || !this.dirty) {
      return false;
    }

    const now = this.now();
    if (now < this.retryNotBefore) {
      this.scheduleFlush(this.retryNotBefore - now);
      return false;
    }

    try {
      const serialized = this.serializePersistedSnapshot(now);
      this.persistentStorage.set(PERSISTENCE_KEY, serialized.raw);
      this.dirty = false;
      this.lastFlushAt = now;
      this.retryNotBefore = 0;
      this.consecutiveFailures = 0;
      this.persistedEventCount = serialized.eventCount;
      this.droppedByPersistedByteLimit = serialized.droppedEvents;
      return true;
    } catch {
      this.consecutiveFailures += 1;
      const retryDelay = Math.min(
        this.retryDelayMs * (2 ** Math.max(0, this.consecutiveFailures - 1)),
        MAX_RETRY_DELAY_MS
      );
      this.retryNotBefore = now + retryDelay;
      this.scheduleFlush(retryDelay);
      return false;
    }
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getPersistenceStatus(): CircularLogPersistenceStatus {
    return {
      identityBound: Boolean(this.identityKey),
      dirty: this.dirty,
      lastFlushAt: this.lastFlushAt || null,
      persistedEventCount: this.persistedEventCount,
      droppedByPersistedByteLimit: this.droppedByPersistedByteLimit,
      consecutiveFailures: this.consecutiveFailures,
    };
  }

  dispose(): void {
    this.cancelScheduledFlush();
  }

  private appendRestored(event: CircularLogEvent): void {
    this.append({
      ...event,
      id: this.nextId++,
      message: event.message.slice(0, LOG_BUFFER_CONFIG.MAX_MESSAGE_LENGTH),
    });
  }

  private resetMemory(): void {
    this.chunkByteCache.clear();
    this.cachedChunkBytes = 0;
    this.storage = new Array(this.capacity).fill(null);
    this.pointer = 0;
    this.size = 0;
    this.nextId = 1;
  }

  private scheduleFlush(delayMs = this.autoFlushMs): void {
    if (!this.persistentStorage || !this.identityKey || !this.dirty || this.flushTimer) {
      return;
    }
    this.flushTimer = this.schedule!(() => {
      this.flushTimer = null;
      this.flush();
    }, Math.max(0, delayMs));
    (this.flushTimer as ReturnType<typeof setTimeout> & { unref?: () => void }).unref?.();
  }

  private cancelScheduledFlush(): void {
    if (!this.flushTimer) return;
    this.cancel!(this.flushTimer);
    this.flushTimer = null;
  }

  private loadPersistedSnapshot(): PersistedCircularLogSnapshot | null {
    if (!this.persistentStorage) return null;
    try {
      const raw = this.persistentStorage.getString(PERSISTENCE_KEY);
      if (!raw) return null;
      if (utf8ByteLength(raw) > this.maxPersistedBytes) {
        this.deletePersistedSnapshot();
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<PersistedCircularLogSnapshot>;
      if (
        parsed.schema !== PERSISTENCE_SCHEMA ||
        typeof parsed.identityKey !== 'string' ||
        typeof parsed.sessionId !== 'string' ||
        !Number.isFinite(parsed.persistedAt) ||
        !Array.isArray(parsed.events) ||
        parsed.events.length > this.capacity ||
        !parsed.events.every(isPersistedEvent) ||
        this.now() - Number(parsed.persistedAt) > this.maxPersistedAgeMs ||
        Number(parsed.persistedAt) - this.now() > 60_000
      ) {
        this.deletePersistedSnapshot();
        return null;
      }
      return parsed as PersistedCircularLogSnapshot;
    } catch {
      this.deletePersistedSnapshot();
      return null;
    }
  }

  private serializePersistedSnapshot(persistedAt: number): {
    raw: string;
    byteCount: number;
    eventCount: number;
    droppedEvents: number;
  } {
    const envelope = JSON.stringify({
      schema: PERSISTENCE_SCHEMA,
      identityKey: this.identityKey!,
      persistedAt,
      sessionId: this.sessionId,
    });
    const prefix = `${envelope.slice(0, -1)},"events":[`;
    let bytes = utf8ByteLength(prefix) + 2;
    const parts: string[] = [];
    const events = this.snapshot();
    let eventCount = 0;
    const start = this.size === this.capacity ? this.pointer : 0;
    // Alinha os lotes às posições do anel para reaproveitar a contagem dos trechos que não mudaram.
    for (let end = events.length; end > 0;) {
      const physicalEnd = (start + end - 1) % this.capacity;
      const chunkLength = Math.min(end, (physicalEnd % 64) + 1);
      const chunk = events.slice(end - chunkLength, end);
      end -= chunkLength;
      const raw = JSON.stringify(chunk).slice(1, -1);
      const chunkBytes = this.getChunkByteLength(Math.floor(physicalEnd / 64), raw) + (parts.length ? 1 : 0);
      if (bytes + chunkBytes <= this.maxPersistedBytes) {
        parts.push(raw);
        bytes += chunkBytes;
        eventCount += chunk.length;
        continue;
      }
      // Só o lote na borda precisa ser contado evento a evento.
      for (let index = chunk.length - 1; index >= 0; index -= 1) {
        const item = JSON.stringify(chunk[index]);
        const itemBytes = utf8ByteLength(item) + (parts.length ? 1 : 0);
        if (bytes + itemBytes > this.maxPersistedBytes) break;
        parts.push(item);
        bytes += itemBytes;
        eventCount += 1;
      }
      break;
    }
    return {
      raw: `${prefix}${parts.reverse().join(',')}]}`,
      byteCount: bytes,
      eventCount,
      droppedEvents: this.size - eventCount,
    };
  }

  private getChunkByteLength(slot: number, raw: string): number {
    const previous = this.chunkByteCache.get(slot);
    // Compara o JSON completo: alterações em data também invalidam a contagem anterior.
    if (previous?.raw === raw) return previous.bytes;
    const bytes = utf8ByteLength(raw);
    this.cachedChunkBytes -= previous?.bytes ?? 0;
    this.chunkByteCache.delete(slot);
    if (bytes <= this.maxPersistedBytes) {
      if (this.cachedChunkBytes + bytes > this.maxPersistedBytes) {
        this.chunkByteCache.clear();
        this.cachedChunkBytes = 0;
      }
      this.chunkByteCache.set(slot, { raw, bytes });
      this.cachedChunkBytes += bytes;
    }
    return bytes;
  }

  private deletePersistedSnapshot(): void {
    if (!this.persistentStorage) return;
    try {
      this.persistentStorage.delete(PERSISTENCE_KEY);
    } catch {
      // A captura em memória deve continuar mesmo se o armazenamento falhar.
    }
  }
}

const createPersistenceStorage = (): CircularLogStorage | null => {
  try {
    return new MMKV({ id: PERSISTENCE_STORAGE_ID });
  } catch {
    return null;
  }
};

const circularBuffer = new CircularLogBuffer(
  LOG_BUFFER_CONFIG.MAX_EVENTS || DEFAULT_CAPACITY,
  { storage: LOG_BUFFER_CONFIG.ENABLED ? createPersistenceStorage() : null }
);
const syncEssentialBuffer = new SupportDiagnosticBuffer({ maxBytes: 64 * 1024, maxEvents: 160 });
let bufferedLogIdentityKey: string | null | undefined;

const consoleLevelMap: Record<ConsoleMethod, CircularLogLevel> = {
  log: 'log',
  info: 'info',
  warn: 'warn',
  error: 'error',
  debug: 'debug',
};

const originalConsole: Partial<Record<ConsoleMethod, (...args: any[]) => void>> = {};
let consoleInterceptInitialized = false;

const sanitizeMessage = (raw: string): string => {
  if (!raw) {
    return raw;
  }

  return sanitizeSupportDiagnosticString(raw);
};

const normalizeArg = (arg: any): string => {
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (typeof arg === 'string') return sanitizeMessage(arg);
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  if (arg instanceof Error) return sanitizeMessage(`${arg.name}: ${arg.message}`);
  try {
    const serialized = JSON.stringify(arg);
    return sanitizeMessage(serialized);
  } catch {
    return '[unserializable]';
  }
};

const recordEvent = (
  level: CircularLogLevel,
  tag: string,
  message: string,
  data?: Record<string, any>
): void => {
  if (!LOG_BUFFER_CONFIG.ENABLED) {
    return;
  }

  if (level === 'error' || level === 'warn') routineLogSummary.flush();

  // Informativos rotineiros não expulsam os marcos e falhas do ciclo de sync.
  if (level === 'error' || level === 'warn' || /syncAll:(start|end)|\[SYNC:MAIN\].*(Iniciando|conclu|falhou)/i.test(message)) {
    syncEssentialBuffer.record({ occurredAt: new Date().toISOString(),
      level: level === 'custom' || level === 'log' ? 'info' : level,
      category: tag, name: 'sync_context', message, data });
  }

  circularBuffer.push({
    level,
    tag,
    message: sanitizeMessage(message).slice(0, LOG_BUFFER_CONFIG.MAX_MESSAGE_LENGTH),
    timestamp: Date.now(),
    data: data === undefined
      ? undefined
      : sanitizeSupportDiagnosticValue(data) as Record<string, any>,
  });
};

const interceptConsole = (): void => {
  if (consoleInterceptInitialized || !LOG_BUFFER_CONFIG.ENABLED) {
    return;
  }

  (['log', 'info', 'warn', 'error', 'debug'] as ConsoleMethod[]).forEach(method => {
    const original = console[method]?.bind(console);
    if (!original) {
      return;
    }
    originalConsole[method] = original;
    console[method] = (...args: any[]) => {
      const message = args.map(normalizeArg).join(' ');
      recordEvent(consoleLevelMap[method], `console.${method}`, message);
      original(...args);
    };
  });

  consoleInterceptInitialized = true;
};

if (LOG_BUFFER_CONFIG.ENABLED) {
  interceptConsole();
}

export const initCircularLogBuffer = (): void => {
  if (LOG_BUFFER_CONFIG.ENABLED) {
    interceptConsole();
  }
};

export const isLogBufferEnabled = (): boolean => LOG_BUFFER_CONFIG.ENABLED;

export const recordBufferedLogEvent = (
  level: CircularLogLevel,
  tag: string,
  message: string,
  data?: Record<string, any>
): void => {
  if (!LOG_BUFFER_CONFIG.ENABLED) {
    return;
  }
  recordEvent(level, tag, message, data);
};

export const getBufferedLogEvents = (): CircularLogEvent[] => {
  if (!LOG_BUFFER_CONFIG.ENABLED) {
    return [];
  }
  routineLogSummary.flush();
  return circularBuffer.snapshot();
};

export const bindBufferedLogIdentity = (identityKey: string | null): number => {
  if (!LOG_BUFFER_CONFIG.ENABLED) {
    return 0;
  }
  // A troca de identidade deve isolar os dois buffers sem apagar o snapshot que será restaurado.
  if (identityKey !== bufferedLogIdentityKey) {
    routineLogSummary.clear();
    syncEssentialBuffer.clear();
    bufferedLogIdentityKey = identityKey;
  }
  return circularBuffer.bindIdentity(identityKey);
};

export const flushBufferedLogEvents = (): boolean => {
  if (!LOG_BUFFER_CONFIG.ENABLED) {
    return false;
  }
  routineLogSummary.flush();
  return circularBuffer.flush();
};

export const getBufferedLogPersistenceStatus = (): CircularLogPersistenceStatus => {
  return circularBuffer.getPersistenceStatus();
};

export const clearBufferedLogEvents = (): void => {
  if (!LOG_BUFFER_CONFIG.ENABLED) {
    return;
  }
  routineLogSummary.clear();
  circularBuffer.clear();
  syncEssentialBuffer.clear();
};

export const getBufferedLogSessionId = (): string => circularBuffer.getSessionId();

export const getSyncEssentialEvents = () => syncEssentialBuffer.createImmutableSnapshot({
  diagnosticId: 'sync-context', createdAt: new Date().toISOString(), metadata: {},
}).events;
